/**
 * Classic Diplomacy Map Visual Data
 *
 * SVG paths and positions for the standard 1901 Diplomacy map
 * Map data derived from Diplomacy.svg by Martin Asal (CC BY-SA 4.0)
 * via https://github.com/elespike/diplomacy_maps
 *
 * Original: https://commons.wikimedia.org/wiki/File:Diplomacy.svg
 */
/**
 * Power colors for the classic map
 */
export const CLASSIC_POWER_COLORS = {
    england: { fill: '#F9A8D4', stroke: '#F472B6' },
    france: { fill: '#60A5FA', stroke: '#3B82F6' },
    germany: { fill: '#9CA3AF', stroke: '#6B7280' },
    italy: { fill: '#22C55E', stroke: '#16A34A' },
    austria: { fill: '#EF4444', stroke: '#DC2626' },
    russia: { fill: '#A855F7', stroke: '#9333EA' },
    turkey: { fill: '#F59E0B', stroke: '#D97706' },
};
/**
 * Neutral territory colors
 */
export const NEUTRAL_COLORS = {
    land: '#D4C4A8',
    sea: '#B8D4E8',
    supplyCenterNeutral: '#FFD700',
};
/**
 * Transform string for paths that need scaling
 * Original paths are in ~610x610 scale, need to transform to 1152x1152
 */
const TRANSFORM = 'matrix(1.8885246,0,0,1.8885246,0.9442593,48.157374)';
/**
 * Visual data for all 75 territories in classic Diplomacy
 * ViewBox: 0 0 1152 1152
 */
export const CLASSIC_TERRITORY_VISUALS = {
    'adr': {
        svgPath: 'm 278,443 -6,-19 -12,-7 1,-16 9,-3 6,1 -1,4 3,7 4,-9 4,1 3,16 17,18 25,18 v 23 l 4,3 h -13 l -25,-24 3,-3 h -10 z',
        transform: TRANSFORM,
        center: { x: 530, y: 867 },
        labelPosition: { x: 540, y: 862 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'aeg': {
        svgPath: 'm 741.24609,939.54102 h -22.66211 l 11.33008,11.33203 -9.4414,-1.88867 1.88867,11.33007 -5.66602,1.88867 -15.10937,-13.21874 -5.66406,11.33007 18.88476,20.77344 -13.2207,-5.66406 -1.88672,5.66406 28.32617,28.32811 1.88867,13.2207 -16.99609,-5.666 1.88867,15.1074 -13.2207,-1.8886 9.44336,32.1054 13.2207,13.2207 7.55273,3.7754 47.21289,1.8887 7.55469,3.7773 35.88281,-35.8808 v -13.2207 l -15.10937,1.8886 -7.55273,-26.4394 -11.33204,-5.666 v -16.99612 l 5.66602,-5.66602 -5.66602,-11.33008 v -5.66601 l -15.10742,1.88867 1.88867,-9.44336 z',
        center: { x: 745, y: 1000 },
        labelPosition: { x: 755, y: 1018 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'alb': {
        svgPath: 'm 335,480 4,7 11,-10 v -6 l -4,-5 v -14 l -9,-6 -7,-1 1,9 v 23 z',
        transform: TRANSFORM,
        center: { x: 628, y: 898 },
        labelPosition: { x: 639, y: 936 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ank': {
        svgPath: 'm 982.97705,880.99672 -11.33115,-5.66557 -5.66557,3.77705 -16.99672,-13.21967 -39.65902,9.44262 -20.77377,16.99672 -11.33115,18.88525 7.5541,7.55409 v 33.99345 l -3.77705,22.66229 h 13.21968 l 32.10491,-20.77377 20.77377,3.77705 13.21968,-3.77705 43.43604,-37.77049 28.3279,3.77705 16.9967,-3.77705 3.7771,-20.77377 -3.7771,-20.77377 -7.5541,-1.88853 z',
        center: { x: 965, y: 921 },
        labelPosition: { x: 971, y: 911 },
        supplyCenterPosition: { x: 940, y: 909 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'apu': {
        svgPath: 'm 318,485 h 4 v -5 l -25,-24 3,-3 h -10 l -12,-10 -4,4 5,4 1,4 -1,3 14,23 11,3 6,-4 z',
        transform: TRANSFORM,
        center: { x: 534, y: 908 },
        labelPosition: { x: 534, y: 898 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'arm': {
        svgPath: 'm 1062.2951,937.65246 -11.3312,-7.5541 -1.8885,-13.21967 3.7771,-20.77377 -3.7771,-20.77377 28.3279,-20.77377 35.8819,28.32787 9.4427,-5.66558 16.9967,3.77705 11.3311,-1.88852 V 979.2 l -47.2131,-28.32787 -39.659,1.88853 z',
        center: { x: 1066, y: 916 },
        labelPosition: { x: 1081, y: 907 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'bal': {
        // Baltic Sea: starts at DEN north coast, traces east coast of DEN south through strait,
        // along KIE/BER/PRU coast, north along LVN/SWE coast, closes NW along SKA/BAL boundary
        svgPath: 'm 469.29883,471.18750 l 28.32812,-1.88867 5.66407,-3.77735 1.88867,24.55079 -1.88867,11.33007 -16.9961,13.22071 -5.66601,15.10742 3.77734,15.10937 -22.66211,13.21875 0,5.66602 22.66211,-13.21875 7.55469,5.66406 1.88867,9.44336 9.44141,1.88867 26.43945,-16.99609 13.2207,1.88672 -1.88867,13.2207 15.10742,1.88867 24.55078,-3.77734 13.22071,-13.21875 22.66211,-1.88867 3.77734,16.99609 11.33008,-1.88867 5.66601,-16.99609 13.22071,-3.77735 7.55273,-15.10937 -1.88867,-11.33008 v -9.44336 l 3.77734,-26.43945 18.88477,-16.9961 h -90.64844 l 1.88867,16.9961 -13.2207,28.32812 -20.77344,1.88867 -9.4414,16.9961 -13.22071,-1.88868 -5.66601,-18.88476 1.88867,9.44336 -5.66406,3.77734 1.88671,7.55274 -13.21874,11.33203 -9.44336,-9.44336 -5.66407,-15.10742 9.44141,-1.88867 1.88867,-5.66602 11.33203,-1.88867 7.55274,1.88867 v -5.66602 l 5.66601,-7.55273 -11.33008,-22.66211 1.88868,-11.33203 -3.77735,-28.32813 -9.44336,-18.88476 -7.55468,15.10742 z',
        center: { x: 574, y: 510 },
        labelPosition: { x: 602, y: 512 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'bar': {
        svgPath: 'm 405,49 -4,-4 -4,3 -6,-1 4,-6 -1,-3 -10,-5 -2,7 -2,-7 -3,-2 -3,7 -3,-5 -5,9 v -9 h -4 V 0 h 178 l -5,9 -5,-3 -13,13 -1,14 -3,5 V 23 l -6,-3 -2,6 -6,7 -7,15 3,10 -7,2 -9,-3 -2,-2 4,-5 -8,-7 -7,2 6,17 6,4 v 8 l -6,-2 -4,2 -11,17 12,9 -2,6 -5,3 -18,-8 -2,9 5,5 7,4 -2,3 -18,-4 -8,-15 v -9 l -12,-6 -2,-5 33,1 12,-5 2,-13 -6,-5 -36,-14 z',
        transform: TRANSFORM,
        center: { x: 804, y: 100 },
        labelPosition: { x: 804, y: 90 },
        labelFontSize: 14,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'bel': {
        svgPath: 'm 169,311 4,-10 18,-2 3,4 12,3 -1,5 3,4 2,11 -5,5 -13,-8 -8,-8 z',
        transform: TRANSFORM,
        center: { x: 355, y: 640 },
        labelPosition: { x: 376, y: 649 },
        supplyCenterPosition: { x: 353, y: 633 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ber': {
        svgPath: 'm 556.17049,567.50164 -15.1082,-1.88853 1.88853,-13.21967 -13.21967,-1.88852 -26.43935,16.99672 v 15.1082 l -7.5541,7.55409 3.77705,11.33115 -5.66557,5.66558 3.77705,26.43934 47.21311,-9.44262 15.1082,-9.44263 1.88853,-7.55409 -9.44263,-11.33115 z',
        center: { x: 499, y: 615 },
        labelPosition: { x: 521, y: 581 },
        supplyCenterPosition: { x: 540, y: 610 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'bla': {
        svgPath: 'm 994.30859,711.0293 -43.43554,24.55078 -33.99414,26.43945 16.99609,16.99609 22.66211,-5.66406 1.88867,9.44141 -18.88476,5.66601 -5.66602,7.55469 -11.33008,1.88867 -3.77734,13.21875 -16.99805,-5.66601 v -9.44141 l -16.99609,-7.55469 7.55468,-13.21875 11.33008,-3.77734 -3.77539,-3.77734 -20.77539,3.77734 -11.33008,-7.55469 3.77735,-3.77734 -3.77735,-3.77734 -24.55078,5.66601 -15.10937,35.88281 1.88867,13.21875 -13.21875,9.44336 -3.77734,26.43946 -1.88868,5.66406 -7.55468,1.88867 -5.66407,26.43945 7.55274,16.99805 30.21484,18.88477 41.54687,-5.66602 11.33203,-18.88477 20.77344,-16.99609 39.66016,-9.44336 16.99609,13.21875 5.66601,-3.77539 11.33008,5.66406 58.54494,-7.55273 7.5547,1.88867 28.3261,-20.77539 5.6661,-18.88476 -11.3301,-16.9961 -24.5508,-3.77734 -49.10156,-22.66211 -20.77539,-1.88867 -13.21875,-13.22071 1.88867,-5.66406 7.55469,1.88867 9.4414,-24.55078 h -5.66601 l -5.66407,-11.33203 24.55079,-20.77344 z',
        center: { x: 920, y: 800 },
        labelPosition: { x: 920, y: 790 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'boh': {
        svgPath: 'm 316,348 -13,-2 -8,3 -3,8 -11,-1 -5,-10 -8,-3 -4,-14 2,-4 12,1 10,-5 9,1 14,12 3,-2 7,7 1,8 z',
        transform: TRANSFORM,
        center: { x: 521, y: 689 },
        labelPosition: { x: 551, y: 695 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'bot': {
        // Gulf of Bothnia: sea zone between Sweden's east coast and Finland's west coast
        svgPath: 'm 373,229 -7,-1 -7,-8 h -48 l 3,-11 8,-3 6,-3 3,-10 -5,-10 -6,-1 1,-21 9,-15 13,-8 8,-10 -4,-7 2,-9 6,-8 7,3 6,1 4,12 -6,1 -7,15 -14,15 2,9 3,5 -2,13 1,6 8,2 8,5 19,-6 18,-8 1,6 8,1 3,3 h -6 l -8,5 -1,5 -12,-1 -16,2 -2,4 -4,2 3,6 4,3 1,8 4,6 z',
        transform: TRANSFORM,
        center: { x: 660, y: 390 },
        labelPosition: { x: 656, y: 417 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'bre': {
        svgPath: 'm 100,322 3,6 6,1 14,15 -1,6 1,7 5,6 18,2 v -28 l 2,-8 2,-10 -6,-1 -2,-6 -6,-2 v 16 l -12,-3 -2,-5 -20,-1 z',
        transform: TRANSFORM,
        center: { x: 205, y: 664 },
        labelPosition: { x: 247, y: 689 },
        supplyCenterPosition: { x: 205, y: 664 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'bud': {
        svgPath: 'm 311,375 11,-5 13,-16 2,-4 13,-3 10,4 8,2 9,7 1,3 6,2 10,11 1,6 6,3 5,11 -5,6 h -14 l -20,4 -2,6 -5,1 -18,-3 -4,2 -3,-2 h -3 l -9,-2 -2,-10 -10,-4 -3,-11 z',
        transform: TRANSFORM,
        center: { x: 622, y: 762 },
        labelPosition: { x: 654, y: 769 },
        supplyCenterPosition: { x: 622, y: 762 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'bul': {
        // Combined polyline128 (south border) + polyline122 (north border) for complete BUL outline
        svgPath: 'm 371,438 -5,1 5,17 -6,5 4,3 h 7 l 12,-4 4,12 8,-4 8,2 5,-6 -1,-10 8,-3 6,-1 -4,-9 3,-14 4,-1 1,-3 -8,-3 h -12 l -6,2 -6,5 -8,-2 -8,2 -7,-4 -5,2 -3,-4 -2,4 3,8 3,5 z',
        transform: TRANSFORM,
        center: { x: 720, y: 913 },
        labelPosition: { x: 752, y: 890 },
        supplyCenterPosition: { x: 720, y: 888 },
        defaultFill: NEUTRAL_COLORS.land,
        coastPaths: [
            {
                id: 'bul_ec',
                svgPath: 'm 805.45574,897.99344 c 0,0 -15.44271,-3.36281 -19.38347,-10.46024 -3.41501,-7.77929 0.66406,-24.0625 0.66406,-24.0625 l 2.04492,-8.74609 c 5.26341,-6.59839 14.3991,-10.1787 24.22859,-7.72133',
                label: 'EC',
                labelPosition: { x: 790, y: 880 },
            },
            {
                id: 'bul_sc',
                svgPath: 'm 741.2459,939.54099 c 1.9e-4,3e-5 0.27564,-11.87049 9.58222,-19.55661 5.18439,-1.94768 11.30143,-1.67793 16.375,-1.23438 7.0571,7.62471 4.25977,17.01367 4.25977,17.01367',
                label: 'SC',
                labelPosition: { x: 758, y: 931 },
            },
        ],
    },
    'bur': {
        svgPath: 'm 208,367 -14,15 -16,-1 v 9 l -5,6 -5,-1 -5,-8 2,-4 -7,-3 -2,-6 9,-9 20,-21 3,-12 4,-9 13,8 -1,7 7,8 2,6 -4,11 z',
        transform: TRANSFORM,
        center: { x: 350, y: 728 },
        labelPosition: { x: 361, y: 720 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'cly': {
        svgPath: 'm 146,200 -2,13 -6,1 -8,-6 -1,-11 10,-8 1,-7 8,-5 14,4 -1,4 -7,3 -2,6 z',
        transform: TRANSFORM,
        center: { x: 255, y: 440 },
        labelPosition: { x: 262, y: 430 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'con': {
        svgPath: 'm 805.45508,897.99414 -11.33008,1.88867 -15.10938,5.66407 1.88868,18.88671 -9.44141,11.33008 3.77734,5.66602 7.37891,2.05273 13.39453,-13.38476 26.43945,-7.55274 9.44141,-9.44336 -18.88476,-5.66601 z m 71.76367,13.21875 -41.54687,5.66602 -5.66602,5.66601 16.99805,1.88867 -43.4375,20.77344 -19.96289,1.05078 -8.36329,12.16797 -1.88867,9.44336 15.10742,-1.88867 v 5.66601 l 11.33204,-3.77734 16.99609,11.33008 37.76953,3.77734 26.43945,-7.55273 3.77735,-22.66406 v -33.99219 z',
        center: { x: 809, y: 902 },
        labelPosition: { x: 854, y: 952 },
        supplyCenterPosition: { x: 817, y: 913 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'den': {
        // Denmark: Jutland peninsula (polygon156) + Funen (polygon250) + Zealand (path1803)
        svgPath: 'm 503.29102,465.52148 -5.66407,3.77735 -28.32812,1.88867 -5.66602,24.55078 -3.77734,18.88477 1.88867,13.21875 18.88477,1.88867 5.66601,-15.10742 16.9961,-13.22071 1.88867,-11.33007 z M 461.74414,527.8418 l -1.88867,5.66601 3.77734,11.33203 -1.88867,13.21875 22.66211,-13.21875 -3.77734,-15.10937 z M 508.95703,507.06836 l -1.88867,5.66602 -9.44141,1.88867 5.66407,15.10742 9.44336,9.44336 13.21874,-11.33203 -1.88671,-7.55274 5.66406,-3.77734 -1.88867,-9.44336 -7.55274,-1.88867 z',
        center: { x: 488, y: 505 },
        labelPosition: { x: 488, y: 487 },
        supplyCenterPosition: { x: 513, y: 525 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'eas': {
        svgPath: 'm 414,552 2,-3 19,-19 6,-4 6,2 6,6 11,-3 2,-10 9,-1 10,8 6,2 14,-4 6,-12 9,3 7,-9 3,1 -5,9 1,12 6,5 -4,24 H 400 v -5 z',
        transform: TRANSFORM,
        center: { x: 822, y: 1075 },
        labelPosition: { x: 892, y: 1075 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'edi': {
        svgPath: 'm 165,210 -7,4 -7,1 6,1 4,2 2,8 -8,2 -10,-11 -1,-4 2,-13 6,-6 6,-1 13,4 -1,5 z',
        transform: TRANSFORM,
        center: { x: 305, y: 466 },
        labelPosition: { x: 301, y: 426 },
        labelFontSize: 10,
        supplyCenterPosition: { x: 305, y: 466 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'eng': {
        svgPath: 'm 173,301 -4,10 -16,4 2,5 -5,-1 -6,-1 -2,-6 -6,-2 v 16 l -12,-3 -2,-5 -20,-1 -14,-14 12,-12 10,1 10,3 4,-4 10,3 13,1 13,3 8,-2 z',
        transform: TRANSFORM,
        center: { x: 195, y: 641 },
        labelPosition: { x: 240, y: 623 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'fin': {
        svgPath: 'm 660.03934,395.6459 15.1082,3.77705 15.1082,9.44262 35.88197,-11.33115 33.99344,-15.10819 18.88524,-30.2164 -3.77704,-16.99672 7.55409,-9.44262 -7.55409,-32.10492 -15.1082,-22.66229 -1.88853,-15.1082 -16.99672,-33.99344 1.88853,-35.88197 -11.33115,-9.44262 1.88852,-13.21968 -3.77705,-5.66557 3.77705,-7.5541 -16.99672,-11.33115 -16.99672,1.88853 -1.88852,22.66229 -26.43935,1.88853 -16.99672,-15.1082 -7.5541,13.21967 26.43935,18.88525 11.33114,67.98689 11.33115,1.88852 7.5541,22.6623 -11.33115,1.88852 -13.21967,28.32787 -26.43934,28.32787 3.77705,16.99672 5.66557,9.44262 -3.77705,24.55082 z',
        center: { x: 715, y: 274 },
        labelPosition: { x: 715, y: 315 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'gal': {
        svgPath: 'm 404,371 -10,5 -10,-11 -6,-2 -1,-3 -9,-7 -8,-2 -10,-4 -13,3 -8,-4 -7,1 -1,-8 1,8 -1,-8 4,1 4,-2 4,-8 h 8 l 3,2 9,-5 3,-4 5,1 6,5 7,-2 5,-3 4,3 2,5 14,6 5,16 -1,6 z',
        transform: TRANSFORM,
        center: { x: 663, y: 694 },
        labelPosition: { x: 691, y: 691 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'gas': {
        svgPath: 'm 142,417 7,-14 8,-6 11,-2 -5,-8 2,-4 -7,-3 -2,-6 -7,-2 -3,-7 -18,-2 -7,19 1,2 -10,15 1,8 10,5 11,5 1,-3 z',
        transform: TRANSFORM,
        center: { x: 240, y: 800 },
        labelPosition: { x: 260, y: 786 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'gol': {
        svgPath: 'm 233,415 5,16 h -14 l -3,3 -10,2 2,15 5,3 v 4 l -4,3 -8,1 -1,4 h -51 l -6,-3 -6,6 h -27 l -5,-8 14,-17 7,-5 15,-1 11,-6 1,-7 v -7 l 11,-6 7,5 12,5 10,-1 13,-5 11,-6 z',
        transform: TRANSFORM,
        center: { x: 303, y: 869 },
        labelPosition: { x: 316, y: 901 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'gre': {
        svgPath: 'm 352,508 15,-1 4,4 -16,-1 -5,4 7,7 2,12 1,-5 7,8 1,-5 8,6 -5,-17 7,1 -1,-8 9,3 -1,-7 -15,-15 1,-3 7,3 -10,-11 3,-6 8,7 3,-1 -1,-6 5,1 -6,-6 h 12 l -4,-12 -12,4 h -7 l -8,3 -5,4 h -6 v 6 l -11,10 7,11 h 4 l -3,2 z',
        transform: TRANSFORM,
        center: { x: 639, y: 975 },
        labelPosition: { x: 679, y: 971 },
        supplyCenterPosition: { x: 705, y: 1012 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'hel': {
        // Heligoland Bight: sea zone west of Denmark/Kiel, north of Holland
        // West boundary uses HEL/NTH curve (path2294) going from ~(396,561) up to ~(453,498)
        // Extends under land (DEN north, KIE east, HOL south) to avoid gaps
        svgPath: 'm 395.84,561.06 c 42.77,-17.29 57.55,-62.57 57.55,-62.57 l 17,-8 0,145 -100,0 0,-74.43 z',
        center: { x: 420, y: 560 },
        labelPosition: { x: 440, y: 555 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'hol': {
        svgPath: 'm 205,311 1,-5 -12,-3 -3,-4 7,-10 7,-13 v 3 h 2 l 4,-5 15,1 1,5 -2,12 -5,6 -5,-1 -2,5 -3,11 -2,2 z',
        transform: TRANSFORM,
        center: { x: 385, y: 600 },
        labelPosition: { x: 403, y: 605 },
        supplyCenterPosition: { x: 389, y: 583 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ion': {
        svgPath: 'm 285,513 v -2 h 4 l 1,3 5,1 13,-15 3,-9 -7,-7 6,-4 8,5 h 4 v -5 h 13 l 4,7 7,11 h 4 l -3,2 5,8 15,-1 4,4 -16,-1 -5,4 7,7 2,12 1,-5 7,8 1,-5 8,6 7,7 -3,3 3,3 17,4 v 5 H 232 l 2,-8 -2,-7 -7,-9 6,-4 5,-7 11,-11 11,6 15,12 8,1 1,-11 z',
        transform: TRANSFORM,
        center: { x: 557, y: 1048 },
        labelPosition: { x: 597, y: 1063 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'iri': {
        svgPath: 'm 109,246 -11,13 -11,-2 -17,4 -12,12 30,30 12,-12 12,-4 10,-6 8,1 -3,-6 -8,-4 h -3 l -1,-7 13,-3 -2,-6 -5,1 11,-7 h 3 l 4,-10 -3,-11 -6,-2 h -10 l -10,5 z',
        transform: TRANSFORM,
        center: { x: 153, y: 590 },
        labelPosition: { x: 163, y: 570 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'kie': {
        // KIE: Original path1820 from SVG, extended to bridge gap with DEN Jutland
        // Starts at DEN Jutland's SW corner, lines SE to original KIE start, follows original path,
        // z closes back to DEN SW corner filling the Schleswig-Holstein gap
        svgPath: 'm 461.74414,527.8418 22.66211,22.66406 -22.66211,13.21875 -16.99609,7.55469 -1.88867,-5.66602 -7.55469,-1.88867 -7.55469,3.77734 1.88867,9.44141 -3.77734,22.66406 -9.44141,11.33008 -9.44336,-1.88867 -3.77734,9.44336 35.88281,11.33008 16.99609,15.10937 3.77735,11.33008 37.77148,-22.66211 -3.77734,-26.43945 5.66601,-5.66602 -3.77734,-11.33008 7.55274,-7.55468 v -15.10743 l -9.44141,-1.88867 -1.88867,-9.44336 z',
        center: { x: 465, y: 590 },
        labelPosition: { x: 450, y: 585 },
        supplyCenterPosition: { x: 475, y: 573 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'lon': {
        svgPath: 'm 178,274 -2,9 -11,10 7,1 -4,2 -8,2 -13,-3 -2,-14 5,-4 3,-6 13,-2 2,1 3,-2 6,2 z',
        transform: TRANSFORM,
        center: { x: 281, y: 600 },
        labelPosition: { x: 288, y: 585 },
        labelFontSize: 10,
        supplyCenterPosition: { x: 314, y: 600 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'lvn': {
        svgPath: 'm 690.25574,578.83279 13.21967,3.77705 13.21967,13.21967 18.88525,-9.44263 5.66557,-13.21967 22.6623,-5.66557 1.88852,-67.98689 7.5541,-20.77377 -7.5541,-20.77377 -20.77377,-22.66229 -22.66229,1.88852 -18.88525,-1.88852 -5.66557,-5.66558 -7.5541,3.77705 5.66557,11.33115 7.5541,5.66557 1.88853,15.1082 7.55409,11.33115 -7.55409,3.77705 -13.21968,-1.88853 -13.21967,-15.10819 -18.88525,16.99672 -3.77704,26.43934 13.21967,15.1082 3.77705,18.88524 11.33114,-1.88852 9.44263,9.44262 z',
        center: { x: 704, y: 524 },
        labelPosition: { x: 704, y: 510 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'lvp': {
        svgPath: 'm 138,214 6,-1 1,4 10,11 v 11 l -4,9 -1,16 -7,-2 h -15 l -2,-6 -5,1 11,-7 h 3 l 4,-10 -3,-11 -6,-2 v -4 l 8,-6 z',
        transform: TRANSFORM,
        center: { x: 278, y: 527 },
        labelPosition: { x: 265, y: 476 },
        labelFontSize: 10,
        supplyCenterPosition: { x: 260, y: 527 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'mao': {
        svgPath: 'm 102,317 -2,5 3,6 6,1 14,15 -1,6 1,7 5,6 -7,19 1,2 -10,15 -11,-3 -5,1 -24,-13 -13,-3 -5,-6 -6,-1 -2,4 -7,-3 -6,6 2,3 -3,12 -2,10 -13,21 h -3 l -4,6 3,7 2,1 -3,9 1,4 -5,8 11,7 8,-1 6,7 1,9 3,6 v 5 l -4,1 -16,22 -17,2 V 273 h 58 z',
        transform: TRANSFORM,
        center: { x: 55, y: 750 },
        labelPosition: { x: 75, y: 680 },
        labelFontSize: 14,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'mar': {
        svgPath: 'm 169,412 -11,6 v 7 l -4,2 -12,-10 7,-14 8,-6 11,-2 5,1 5,-6 v -9 l 16,1 3,3 6,-6 4,7 -3,4 3,6 -6,3 3,3 -1,8 8,6 -13,5 -10,1 -12,-5 z',
        transform: TRANSFORM,
        center: { x: 341, y: 813 },
        labelPosition: { x: 313, y: 816 },
        supplyCenterPosition: { x: 341, y: 831 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'mos': {
        svgPath: 'm 1009.4164,357.87541 -35.88197,9.44262 -49.10164,28.32787 -24.55082,-1.88852 -33.99344,20.77377 -3.77705,24.55082 1.88852,5.66557 -11.33115,5.66557 -7.55409,-7.55409 -15.1082,3.77704 -20.77377,26.43935 -13.21967,7.5541 -22.6623,-1.88853 -7.5541,20.77377 -1.88852,67.98689 -22.6623,5.66557 -5.66557,13.21967 -18.88525,9.44263 13.21967,35.88197 7.5541,-5.66558 124.64263,-26.43934 22.66229,5.66557 16.99672,-11.33115 32.10492,11.33115 20.77377,-28.32787 20.77377,11.33115 18.88525,1.88852 13.2197,-7.55409 30.2164,1.88852 9.4426,37.77049 18.8852,1.88853 9.4427,30.21639 52.8786,16.99672 h 22.6623 V 269.11475 l -20.7737,28.32787 -47.2131,20.77377 -16.9968,30.2164 z',
        center: { x: 922, y: 486 },
        labelPosition: { x: 954, y: 493 },
        labelFontSize: 14,
        supplyCenterPosition: { x: 922, y: 486 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'mun': {
        svgPath: 'm 284,314 4,-9 -25,5 -20,12 h -6 l -18,22 -8,2 2,6 -4,11 13,2 3,-3 7,1 2,3 9,4 3,-1 4,2 17,-3 4,2 -2,-8 h 6 l 6,-6 -5,-10 -8,-3 -4,-14 2,-4 12,1 10,-5 z',
        transform: TRANSFORM,
        center: { x: 478, y: 710 },
        labelPosition: { x: 456, y: 730 },
        supplyCenterPosition: { x: 493, y: 728 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'naf': {
        svgPath: 'm 0,520 v 39 h 195 l 2,-32 6,-7 -24,-5 -10,3 -19,-7 -33,-2 -11,2 -7,4 -10,-3 -5,6 -5,2 -11,-4 v -5 l -4,3 -18,-5 -4,-7 -1,-8 -4,1 -4,1 -16,22 z',
        transform: TRANSFORM,
        center: { x: 198, y: 1051 },
        labelPosition: { x: 198, y: 1067 },
        labelFontSize: 14,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'nao': {
        svgPath: 'm 95,218 -1,-2 h 3 l 4,-4 h 9 l 9,5 1,10 h 10 v -4 l 8,-6 v -3 l -8,-6 -1,-11 10,-8 1,-7 8,-5 V 0 H 0 v 273 h 58 l 12,-12 -6,-11 3,-8 4,3 10,-11 -7,-6 6,-3 -2,-7 4,-1 7,3 h 5 z',
        transform: TRANSFORM,
        center: { x: 82, y: 365 },
        labelPosition: { x: 82, y: 355 },
        labelFontSize: 14,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'nap': {
        svgPath: 'm 311,491 -7,-7 -11,-3 -14,-23 -8,6 5,10 14,13 4,15 -5,9 1,3 5,1 13,-15 z',
        transform: TRANSFORM,
        center: { x: 499, y: 917 },
        labelPosition: { x: 569, y: 990 },
        supplyCenterPosition: { x: 525, y: 945 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'nth': {
        svgPath: 'm 374.83858,338.99008 c -28.16113,-2e-5 -50.99024,22.8291 -50.9902,50.99023 v 30.2168 l -1.8887,9.4414 -9.4414,15.10938 -13.2207,7.55469 -13.2188,1.88671 11.3301,1.88868 7.5547,3.77734 3.7773,15.10938 v 24.55078 l 9.4415,13.21875 3.7773,11.33203 -1.8887,24.55078 -5.664,7.55273 3.7754,1.88867 5.666,-3.77734 11.332,3.77734 1.8887,7.55469 -3.7774,16.9961 -20.7734,18.88671 13.2187,1.88672 -7.5546,3.77735 9.4433,9.44336 33.9942,-3.77735 13.2187,-18.88476 13.2207,-24.55078 v 5.66601 h 3.7774 l 7.5527,-9.44336 l -3.5454,-4.5551 c 42.77228,-17.28713 57.55297,-62.56553 57.55297,-62.56553 l 10.2033,-2.7544 l 5.6641,-24.55078 h -20.48226 v -34.16558 l -11.62124,-9.27192 -3.7774,-16.99609 7.5547,-11.33008 -3.7773,-11.33203 3.7773,-24.55078 7.5527,-13.21875 -1.8886,-11.33203 z',
        center: { x: 358, y: 498 },
        labelPosition: { x: 358, y: 488 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'nwg': {
        svgPath: 'm 158,193 -6,1 2,-6 7,-3 1,-4 -14,-4 V 0 h 214 v 33 l -5,6 -14,5 -19,10 -4,10 -10,11 -1,9 -6,2 -11,25 -15,21 -8,2 -5,8 -6,-1 -22,13 h -38 l -27,27 v 16 z',
        transform: TRANSFORM,
        center: { x: 416, y: 225 },
        labelPosition: { x: 416, y: 215 },
        labelFontSize: 14,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'nwy': {
        // Full Norway polygon (polygon332 from SVG): western Scandinavian coast from Arctic to Skagerrak
        svgPath: 'm 301,132 -1,-6 9,-11 -1,-11 3,-3 13,-30 8,3 -2,-10 11,1 1,-4 4,-7 9,8 14,-1 1,-12 9,-1 9,6 -2,4 2,3 9,-13 -6,-1 4,-6 -1,-3 -10,-5 -2,7 -2,-7 -3,-2 -3,7 -3,-5 -5,9 v -9 h -4 l -5,6 -14,5 -19,10 -4,10 -10,11 -1,9 -6,2 -11,25 -15,21 -8,2 -5,8 -6,-1 -22,13 1,6 -4,7 -2,13 2,6 -4,6 2,9 10,8 5,1 20,-9 4,-8 5,10 4,1 8,-27 -2,-7 5,-6 2,-31 z',
        transform: TRANSFORM,
        center: { x: 476, y: 378 },
        labelPosition: { x: 480, y: 406 },
        supplyCenterPosition: { x: 511, y: 400 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'par': {
        svgPath: 'm 146,365 3,7 7,2 9,-9 20,-21 3,-12 -16,-4 -7,3 h -6 l -11,-2 -2,8 z',
        transform: TRANSFORM,
        center: { x: 291, y: 700 },
        labelPosition: { x: 291, y: 703 },
        supplyCenterPosition: { x: 317, y: 700 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'pic': {
        svgPath: 'm 169,311 -16,4 2,5 -5,-1 -2,10 11,2 h 6 l 7,-3 16,4 4,-9 -8,-8 z',
        transform: TRANSFORM,
        center: { x: 294, y: 667 },
        labelPosition: { x: 324, y: 658 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'pie': {
        svgPath: 'm 233,404 13,-12 -3,-4 -14,-3 -2,5 -6,-5 -8,2 -6,-1 -3,4 3,6 -6,3 3,3 -1,8 8,6 11,-6 11,5 3,-4 z',
        transform: TRANSFORM,
        center: { x: 394, y: 819 },
        labelPosition: { x: 412, y: 805 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'por': {
        svgPath: 'm 62,407 -7,-7 -13,-1 1,-4 -11,1 -2,10 -13,21 h -3 l -4,6 3,7 2,1 -3,9 1,4 -5,8 11,7 8,-1 9,-11 -2,-10 6,-6 -3,-10 5,1 10,-20 9,-1 z',
        transform: TRANSFORM,
        center: { x: 50, y: 898 },
        labelPosition: { x: 53, y: 872 },
        supplyCenterPosition: { x: 32, y: 868 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'pru': {
        svgPath: 'm 644.93115,590.16393 7.5541,3.77705 26.43934,-5.66557 11.33115,-9.44262 3.77705,-30.2164 -9.44263,-9.44262 -11.33114,1.88852 -3.77705,-18.88524 -13.21967,-15.1082 v 9.44262 l 1.88852,11.33115 -7.5541,15.1082 -13.21967,3.77705 -5.66557,16.99672 -11.33115,1.88852 -3.77705,-16.99672 -22.6623,1.88853 -13.21967,13.21967 -24.55082,3.77705 -3.77705,28.32787 9.44263,11.33115 -1.88853,7.55409 45.32459,5.66558 7.5541,-7.5541 3.77705,-13.21967 z',
        center: { x: 603, y: 598 },
        labelPosition: { x: 603, y: 584 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'rom': {
        svgPath: 'm 279,458 1,-3 -1,-4 -5,-4 -11,-13 -13,4 -3,4 1,5 8,11 15,6 z',
        transform: TRANSFORM,
        center: { x: 476, y: 894 },
        labelPosition: { x: 506, y: 906 },
        supplyCenterPosition: { x: 476, y: 894 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ruh': {
        svgPath: 'm 211,346 8,-2 18,-22 h 6 l -2,-6 -9,-8 -19,-6 -3,11 -2,2 2,11 -5,5 -1,7 z',
        transform: TRANSFORM,
        center: { x: 415, y: 660 },
        labelPosition: { x: 415, y: 646 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'rum': {
        svgPath: 'm 390,425 8,2 6,-5 6,-2 h 12 l 8,3 2,-14 7,-5 -1,-7 -11,2 -5,-17 1,-6 -9,-4 -3,-11 -8,-1 1,11 -10,5 1,6 6,3 5,11 -5,6 h -14 l -20,4 -2,6 2,9 3,4 5,-2 7,4 z',
        transform: TRANSFORM,
        center: { x: 757, y: 769 },
        labelPosition: { x: 760, y: 829 },
        supplyCenterPosition: { x: 790, y: 825 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ser': {
        svgPath: 'm 371,456 -5,-17 5,-1 -3,-5 -3,-8 2,-4 -2,-9 -5,1 -18,-3 -4,2 -3,-2 h -3 l -2,6 1,8 -4,5 3,8 7,9 9,6 v 14 l 4,5 h 6 l 5,-4 8,-3 -4,-3 z',
        transform: TRANSFORM,
        center: { x: 636, y: 875 },
        labelPosition: { x: 648, y: 875 },
        supplyCenterPosition: { x: 648, y: 857 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'sev': {
        svgPath: 'm 1066.0721,624.15738 -18.8852,-1.88853 -9.4426,-37.77049 -30.2164,-1.88852 -13.2197,7.55409 -18.88525,-1.88852 -20.77377,-11.33115 -20.77377,28.32787 -32.10492,-11.33115 -16.99672,11.33115 3.77705,15.1082 -7.5541,7.5541 -11.33114,71.76393 -28.32787,9.44262 -20.77377,18.88525 -3.77705,22.6623 -16.99672,7.55409 -1.88853,11.33115 9.44262,32.10492 20.77377,-3.77705 15.1082,-35.88197 24.55082,-5.66557 3.77705,3.77705 -3.77705,3.77705 11.33115,7.5541 20.77377,-3.77705 3.77705,3.77705 -11.33115,3.77704 -7.5541,13.21968 16.99672,7.5541 v 9.44262 l 16.99672,5.66557 3.77705,-13.21967 11.33115,-1.88852 5.66558,-7.5541 18.88524,-5.66558 -1.88852,-9.44262 -22.6623,5.66557 -16.99672,-16.99672 33.99344,-26.43934 43.43607,-24.55082 1.88852,5.66557 -24.55082,20.77377 5.66558,11.33115 h 5.66557 l -9.44262,24.55082 -7.5541,-1.88852 -1.88852,5.66557 13.21967,13.21967 20.77377,1.88853 49.10165,22.66229 24.5508,3.77705 11.3312,16.99672 -5.6656,18.88525 35.8819,28.32787 9.4427,-5.66558 16.9967,3.77705 11.3311,-1.88852 V 671.37049 h -22.6623 l -52.8786,-16.99672 z',
        center: { x: 916, y: 816 },
        labelPosition: { x: 909, y: 755 },
        labelFontSize: 14,
        supplyCenterPosition: { x: 916, y: 816 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'sil': {
        svgPath: 'm 544.83934,654.37377 16.99673,1.88853 26.43934,22.66229 5.66557,-3.77705 13.21968,13.21967 7.55409,1.88853 7.5541,-3.77705 7.5541,-15.1082 -13.21967,-5.66557 -5.66557,-9.44262 -5.66558,-35.88197 -45.32459,-5.66558 -15.1082,9.44263 -7.55409,16.99672 z',
        center: { x: 544, y: 639 },
        labelPosition: { x: 579, y: 650 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ska': {
        // Skagerrak: narrow strait between NWY south coast, SWE west coast, DEN north coast
        // Extends ~10-15 units under adjacent land to prevent gaps; land renders on top
        svgPath: 'm 222,190 13,10 11,6 24,-10 10,4 2,30 -34,0 -26,-5 z',
        transform: TRANSFORM,
        center: { x: 477, y: 455 },
        labelPosition: { x: 485, y: 457 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'smy': {
        svgPath: 'm 799.79017,1011.3049 7.55409,26.4394 15.1082,-1.8886 v 13.2197 l 11.33115,-7.5541 11.33115,3.7771 11.33114,11.3311 20.77377,-5.6656 3.77705,-18.8852 16.99672,-1.8885 18.88525,15.1082 11.33115,3.777 26.43934,-7.5541 11.33115,-22.6623 16.99672,5.6656 13.21967,-16.9967 5.66558,1.8885 11.3311,-28.32787 16.9968,-15.1082 18.8852,-3.77705 15.1082,-9.44262 -1.8885,-15.1082 -11.3312,-7.5541 -1.8885,-13.21967 -16.9967,3.77705 -28.3279,-3.77705 -43.43604,37.77049 -13.21968,3.77705 -20.77377,-3.77705 -32.10491,20.77377 h -13.21968 l -26.43934,7.5541 -37.77049,-3.77705 -16.99672,-11.33115 -11.33115,3.77705 5.66557,11.33115 -5.66557,5.66557 v 16.99668 z',
        center: { x: 802, y: 1007 },
        labelPosition: { x: 837, y: 1019 },
        labelFontSize: 14,
        supplyCenterPosition: { x: 806, y: 1013 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'spa': {
        svgPath: 'm 134,417 -11,-5 -10,-5 -1,-8 -11,-3 -5,1 -24,-13 -13,-3 -5,-6 -6,-1 -2,4 -7,-3 -6,6 2,3 -3,12 11,-1 -1,4 13,1 7,7 -1,4 -9,1 -10,20 -5,-1 3,10 -6,6 2,10 -9,11 6,7 1,9 3,6 10,-2 5,1 8,-3 18,5 5,3 3,-9 4,-2 8,1 9,-10 6,-1 2,-4 -5,-8 14,-17 7,-5 15,-1 11,-6 1,-7 -4,2 -12,-10 -7,-3 -1,3 -11,-5 z',
        transform: TRANSFORM,
        center: { x: 127, y: 853 },
        labelPosition: { x: 163, y: 885 },
        labelFontSize: 14,
        supplyCenterPosition: { x: 137, y: 880 },
        defaultFill: NEUTRAL_COLORS.land,
        coastPaths: [
            {
                id: 'spa_nc',
                svgPath: 'm 212.45901,801.67869 c -9.33415,7.93357 -32.86454,8.68917 -32.86454,8.68917 -14.65881,-2.25548 -25.09897,-14.08097 -38.43627,-19.61981 -10.91053,-5.81213 -23.45491,-7.05662 -34.75195,-11.54493 -3.76177,-1.53731 -6.865944,-5.79611 -9.505859,-7.52148 -4.799522,3.65572 -11.28101,2.81434 -16.794922,1.91992 0.705207,2.60377 1.076172,7.57813 1.076172,7.57813 -3.96873,7.69073 -19.804595,14.83343 -19.804595,14.83343',
                label: 'NC',
                labelPosition: { x: 170, y: 808 },
            },
            {
                id: 'spa_sc',
                svgPath: 'm 51.934423,931.98689 c 0,0 16.198271,-3.38407 20.331202,-0.43416 6.418204,5.93871 7.802252,15.16887 8.025391,23.48047 9.990045,-0.41495 20.452354,0.85064 29.769534,-3.50195 13.31103,-2.04935 26.35085,4.53086 38.34375,7.85937 4.61823,-10.52976 17.22937,-16.81292 28.29882,-13.30664 7.19621,-0.31077 8.69672,-8.39142 12.76563,-12.49609 2.00365,-2.5903 8.56804,-2.36583 5.43359,-6.4043 -3.9276,-11.87902 3.91047,-23.13879 11.8726,-31.19286 10.81589,-12.51333 20.56478,-27.72198 37.18795,-33.0044 7.96625,-3.34153 16.72714,-2.88336 25.06055,-2.7793 6.14517,-0.0889 22.75361,-5.64965 22.75361,-5.64965',
                label: 'SC',
                labelPosition: { x: 120, y: 963 },
            },
        ],
    },
    'stp': {
        svgPath: 'm 782.79344,325.77049 -7.55409,9.44262 3.77704,16.99672 -18.88524,30.2164 1.88852,11.33115 15.1082,1.88852 5.66557,5.66557 H 771.4623 l -15.1082,9.44263 -1.88853,9.44262 -22.66229,-1.88852 -30.21639,3.77704 -3.77705,7.5541 5.66557,5.66558 18.88525,1.88852 22.66229,-1.88852 20.77377,22.66229 7.5541,20.77377 22.6623,1.88853 13.21967,-7.5541 20.77377,-26.43935 15.1082,-3.77704 7.55409,7.55409 11.33115,-5.66557 -1.88852,-5.66557 3.77705,-24.55082 33.99344,-20.77377 24.55082,1.88852 49.10164,-28.32787 35.88197,-9.44262 56.6557,-9.44262 16.9968,-30.2164 47.2131,-20.77377 20.7737,-28.32787 V 48.157374 H 1020.74754 L 1011.30492,65.15410 L 1001.86230,59.48852 L 977.31148,84.03934 L 975.42295,110.47869 L 969.75738,119.92131 L 969.75738,91.59344 L 958.42623,85.92787 L 954.64918,97.25901 L 943.31803,110.47869 L 930.09836,138.80655 L 935.76394,157.69180 L 922.54426,161.46885 L 905.54754,155.80328 L 901.77049,152.02623 L 909.32459,142.58360 L 894.21640,129.36393 L 880.99672,133.14098 L 892.32787,165.24590 L 903.65902,172.80000 L 903.65902,187.90819 L 892.32787,184.13115 L 884.77377,187.90819 L 864.00000,220.01311 L 886.66230,237.00983 L 882.88525,248.34098 L 873.44262,254.00656 L 839.44918,238.89836 L 835.67213,255.89508 L 845.11476,265.33770 L 858.33443,272.89180 L 854.55738,278.55738 L 820.56394,271.00328 L 805.45574,242.67541 L 805.45574,225.67869 L 782.79344,214.34754 L 779.01639,204.90492 L 841.33771,206.79344 L 864.00000,197.35082 L 867.77705,172.80000 L 856.44590,163.35737 L 788.45902,136.91803 L 765.79672,140.69508 L 716.69508,138.80655 l 16.99672,11.33115 -3.77705,7.5541 3.77705,5.66557 -1.88852,13.21968 11.33115,9.44262 -1.88853,35.88197 16.99672,33.99344 1.88853,15.1082 15.1082,22.66229 7.55409,32.10492 z',
        center: { x: 768, y: 437 },
        labelPosition: { x: 800, y: 444 },
        labelFontSize: 14,
        supplyCenterPosition: { x: 768, y: 437 },
        defaultFill: NEUTRAL_COLORS.land,
        coastPaths: [
            {
                id: 'stp_nc',
                svgPath: 'm 1020.7475,48.157374 10.5826,18.000829 c -1.4219,2.560547 -2.8438,5.121094 -4.2656,7.681641 -3.9382,7.957598 -14.0786,9.789 -22.0508,8.107422 -6.1359,4.602974 -12.89378,11.303788 -10.61526,19.761714 -0.32761,13.94573 -6.41162,30.9268 -21.4043,34.64063 -7.03383,1.22664 -14.20884,-2.57399 -18.19531,-8.28906 -3.8832,6.04548 -7.34279,13.85396 -2.5918,20.47461 4.39712,11.58788 -3.11059,25.17486 -15.08008,28.02343 -4.72799,1.56208 -9.73943,2.09555 -14.70117,2.05079 2.23426,12.88188 -7.84473,28.37982 -21.99805,26.69921 -2.41221,0.0749 -6.09965,-3.02263 -6.33799,1.30071 -0.94553,2.99073 -6.60813,7.00375 -4.68573,9.46143 10.11882,6.4283 18.48679,19.15321 13.33622,31.28865 -3.81054,11.95234 -14.20804,22.49255 -27.04492,23.85937 2.11128,16.85322 -17.86345,30.46245 -33.0664,23.48047 -11.09024,-4.35902 -24.73509,-2.72596 -33.88868,-11.53125 -8.35965,-7.54128 -11.80547,-18.65157 -17.11132,-28.27149 -2.4739,-5.65264 -4.19861,-11.73958 -4.4375,-17.90429 -14.46288,-4.98825 -28.63727,-19.37376 -24.94141,-35.79102 3.02435,-11.96686 17.17319,-17.0116 28.29297,-14.33984 19.1413,0.29971 38.82719,4.97756 57.10742,-2.20117 2.71626,-2.93983 0.32553,-5.86957 -3.02642,-6.38856 -18.57561,-8.25499 -38.24808,-14.29863 -56.98335,-22.21105 -9.09299,-0.56677 -18.38012,4.23218 -27.26367,1.00781 -9.46734,-8.48425 -9.68842,-18.26181 -9.68842,-18.26181',
                label: 'NC',
                labelPosition: { x: 913, y: 174 },
            },
            {
                id: 'stp_sc',
                svgPath: 'm 760.13115,382.42623 c 0,0 11.38509,-6.57033 23.31612,-3.50826 13.47496,4.06199 21.91572,23.13454 11.54296,34.32812 -5.40852,6.80594 -15.08342,4.4647 -22.01953,7.69727 -2.74725,3.32838 -3.10144,8.38872 -5.94531,11.43164 -8.05486,3.53589 -22.00244,2.92992 -22.00244,2.92992',
                label: 'SC',
                labelPosition: { x: 787, y: 398 },
            },
        ],
    },
    'swe': {
        svgPath: 'm 275,203 2,15 -1,6 6,12 -3,4 v 3 l 3,10 7,1 5,-9 11,-1 7,-15 -1,-9 3,-11 8,-3 6,-3 3,-10 -5,-10 -6,-1 1,-21 9,-15 13,-8 8,-10 -4,-7 2,-9 6,-8 7,3 -6,-36 -14,-10 -1,4 -11,-1 2,10 -8,-3 -13,30 -3,3 1,11 -9,11 1,6 -9,1 -2,31 -5,6 2,7 -8,27 z',
        transform: TRANSFORM,
        center: { x: 572, y: 355 },
        labelPosition: { x: 584, y: 417 },
        supplyCenterPosition: { x: 609, y: 415 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'syr': {
        svgPath: 'm 1151.0557,979.2 v 124.6426 H 998.08525 l 7.55405,-45.3246 -11.3311,-9.4426 -1.88853,-22.6623 9.44263,-16.9967 11.3311,-28.32787 16.9968,-15.1082 18.8852,-3.77705 15.1082,-9.44262 39.659,-1.88853 z',
        center: { x: 1047, y: 1015 },
        labelPosition: { x: 1054, y: 1031 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'tri': {
        // Trieste: Small coastal territory between Venice and Vienna
        // Using transformed coordinates (needs matrix transform)
        svgPath: 'm 294,380 -5,5 -13,1 3,3 -3,10 -1,4 3,7 4,-9 4,1 3,16 17,18 25,18 -1,-9 7,1 -7,-9 -3,-8 4,-5 -1,-8 2,-6 -9,-2 -2,-10 -10,-4 -3,-11 -9,2 z',
        transform: TRANSFORM,
        center: { x: 539, y: 802 },
        labelPosition: { x: 569, y: 806 },
        supplyCenterPosition: { x: 539, y: 802 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'tun': {
        svgPath: 'm 203,520 -6,7 -2,32 h 37 l 2,-8 -2,-7 -7,-9 6,-4 5,-7 -3,-1 -9,4 -1,-9 -5,-2 -6,1 -4,4 z',
        transform: TRANSFORM,
        center: { x: 383, y: 1079 },
        labelPosition: { x: 405, y: 1081 },
        supplyCenterPosition: { x: 418, y: 1053 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'tus': {
        svgPath: 'm 236,411 -3,4 5,16 9,11 3,-4 13,-4 -10,-16 -7,-2 -6,-1 z',
        transform: TRANSFORM,
        center: { x: 442, y: 833 },
        labelPosition: { x: 464, y: 853 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'tyr': {
        svgPath: 'm 241,378 -7,-4 v -8 l 9,4 3,-1 4,2 17,-3 4,2 -2,-8 h 6 l 6,-6 11,1 3,5 -1,18 -5,5 -13,1 -8,-1 -9,3 -4,6 -5,3 -4,-5 -3,-4 2,-4 z',
        transform: TRANSFORM,
        center: { x: 480, y: 772 },
        labelPosition: { x: 500, y: 766 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'tys': {
        svgPath: 'm 276,510 h -13 l -6,-3 -5,1 -5,5 -11,11 -3,-1 -9,4 -1,-9 -5,-2 v -26 h 2 l 4,-22 -2,-10 h -4 v -4 l 5,-4 2,-6 v -8 l -1,-5 h 14 l 9,11 1,5 8,11 15,6 5,10 14,13 4,15 -5,9 h -4 v -3 z',
        transform: TRANSFORM,
        center: { x: 430, y: 936 },
        labelPosition: { x: 455, y: 971 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'ukr': {
        svgPath: 'm 782.79344,750.68853 16.99673,7.55409 16.99672,-7.55409 3.77705,-22.6623 20.77377,-18.88525 28.32787,-9.44262 11.33114,-71.76393 7.5541,-7.5541 -3.77705,-15.1082 -22.66229,-5.66557 -124.64263,26.43934 -7.5541,5.66558 -5.66557,33.99344 3.77705,9.44262 26.43934,11.33115 9.44263,30.21639 -1.88853,11.33115 15.1082,1.88852 z',
        center: { x: 778, y: 672 },
        labelPosition: { x: 808, y: 680 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'ven': {
        svgPath: 'm 270,398 6,1 3,-10 -3,-3 -8,-1 -9,3 -4,6 -5,3 -4,-5 -13,12 3,7 4,4 6,1 7,2 10,16 11,13 4,-4 -6,-19 -12,-7 1,-16 z',
        transform: TRANSFORM,
        center: { x: 457, y: 830 },
        labelPosition: { x: 465, y: 817 },
        supplyCenterPosition: { x: 493, y: 817 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'vie': {
        svgPath: 'm 303,346 13,2 6,-1 7,-1 8,4 -2,4 -13,16 -11,5 -3,8 -9,2 -5,-5 1,-18 -3,-5 3,-8 z',
        transform: TRANSFORM,
        center: { x: 563, y: 733 },
        labelPosition: { x: 592, y: 740 },
        supplyCenterPosition: { x: 563, y: 733 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'wal': {
        svgPath: 'm 128,262 h 15 l 7,2 3,7 -3,6 -5,4 2,14 -13,-1 -10,-3 -4,4 -10,-3 -10,-1 12,-4 10,-6 8,1 -3,-6 -8,-4 h -3 l -1,-7 z',
        transform: TRANSFORM,
        center: { x: 234, y: 556 },
        labelPosition: { x: 256, y: 569 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
    'war': {
        svgPath: 'm 650.59672,675.14754 -5.66557,-3.77705 h -15.1082 l -13.21967,-5.66557 -5.66557,-9.44262 -5.66558,-35.88197 7.5541,-7.5541 3.77705,-13.21967 28.32787,-9.44263 7.5541,3.77705 26.43934,-5.66557 11.33115,-9.44262 13.21967,3.77705 13.21967,13.21967 13.21967,35.88197 -5.66557,33.99344 -7.5541,-5.66558 -9.44262,5.66558 -13.21967,3.77705 -11.33115,-9.44263 -9.44262,-1.88852 -5.66558,7.5541 z',
        center: { x: 656, y: 634 },
        labelPosition: { x: 656, y: 644 },
        supplyCenterPosition: { x: 656, y: 622 },
        defaultFill: NEUTRAL_COLORS.land,
    },
    'wes': {
        svgPath: 'm 37,490 10,-2 5,1 8,-3 18,5 5,3 3,-9 4,-2 8,1 9,-10 6,-1 2,-4 h 27 l 8,2 4,-5 h 51 l 1,10 -2,9 4,7 h 4 l 5,-3 1,1 v 26 l -6,1 -4,4 -5,-1 -24,-5 -10,3 -19,-7 -33,-2 -11,2 -7,4 -10,-3 -5,6 -5,2 -11,-4 v -5 l -4,3 -18,-5 -4,-7 -1,-8 -4,1 z',
        transform: TRANSFORM,
        center: { x: 181, y: 994 },
        labelPosition: { x: 261, y: 988 },
        defaultFill: NEUTRAL_COLORS.sea,
    },
    'yor': {
        svgPath: 'm 168,246 2,6 -1,13 -3,4 -13,2 -3,-7 1,-16 4,-9 v -11 l 8,-2 v 13 z',
        transform: TRANSFORM,
        center: { x: 297, y: 558 },
        labelPosition: { x: 301, y: 548 },
        labelFontSize: 10,
        defaultFill: NEUTRAL_COLORS.land,
    },
};
/**
 * List of all supply center territory IDs (34 total)
 */
export const SUPPLY_CENTER_IDS = [
    // England (3)
    'edi', 'lvp', 'lon',
    // France (3)
    'bre', 'par', 'mar',
    // Germany (3)
    'kie', 'ber', 'mun',
    // Italy (3)
    'ven', 'rom', 'nap',
    // Austria (3)
    'vie', 'tri', 'bud',
    // Russia (4)
    'stp', 'mos', 'war', 'sev',
    // Turkey (3)
    'con', 'ank', 'smy',
    // Neutrals (12)
    'nwy', 'swe', 'den', 'hol', 'bel', 'spa', 'por', 'tun', 'ser', 'gre', 'bul', 'rum',
];
/**
 * Complete classic map data
 */
export const CLASSIC_MAP_DATA = {
    id: 'classic',
    name: 'Classic Diplomacy Map',
    viewBox: { width: 1152, height: 1152 },
    territories: CLASSIC_TERRITORY_VISUALS,
    powerColors: CLASSIC_POWER_COLORS,
};
//# sourceMappingURL=classicVisualData.js.map